export function ProgramVisual({ idPrefix }: { idPrefix: string }) {
	const phases = [
		{ label: 'Universidad', routes: 5 },
		{ label: 'TFM y habilitación', routes: 5 },
		{ label: 'Colegiación', routes: 5 },
		{ label: 'Primer encargo', routes: 6 },
		{ label: 'Posicionamiento', routes: 6 },
	]
	const spineX = 158
	const startY = 86
	const gapY = 54
	const endY = startY + (phases.length - 1) * gapY
	const routeStartX = 210
	const routeSpacing = 36
	const ballDur = 3.2
	const phaseInterval = ballDur / (phases.length - 1)
	const lineDrawDur = 0.45
	const subdotDur = 0.35
	const subdotSpread = 0.55
	const gradientId = `${idPrefix}-program-spine`

	const intensitySeq = [
		0.95, 0.58, 0.88, 0.68, 0.92, 0.5, 0.82, 0.62, 0.78, 0.72,
	]
	const dotIntensity = (phaseIndex: number, routeIndex: number) =>
		intensitySeq[(routeIndex * 2 + phaseIndex * 7) % intensitySeq.length] ??
		0.75

	// Irregular per-(phase, route) offsets — each subnode lights up at a
	// pseudo-random moment within the spread window after its phase activates.
	const dotOffset = (phaseIndex: number, routeIndex: number) => {
		const seed =
			(phaseIndex * 73 + routeIndex * 137 + phaseIndex * routeIndex * 17 + 11) %
			100
		return (seed / 100) * subdotSpread
	}

	return (
		<svg
			viewBox="0 0 560 420"
			className="h-full w-full"
			role="img"
			aria-label="Programa: de estudiante a arquitecto"
			style={{ color: 'var(--gold-text)' }}
		>
			<defs>
				<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor="currentColor" stopOpacity="0.55" />
					<stop offset="100%" stopColor="currentColor" stopOpacity="0.15" />
				</linearGradient>
			</defs>

			<text
				x="280"
				y="30"
				textAnchor="middle"
				fontSize="12"
				fill="rgba(255,255,255,0.42)"
				fontFamily="ui-monospace, SFMono-Regular, monospace"
				letterSpacing="2"
			>
				PROGRAMA DE TRANSICIÓN · 27 RUTAS
			</text>

			<line
				x1={spineX}
				y1={startY}
				x2={spineX}
				y2={endY}
				stroke={`url(#${gradientId})`}
				strokeWidth="2"
				strokeLinecap="round"
			/>

			<circle cx={spineX} cy={startY} r="3.5" fill="currentColor">
				<animate
					attributeName="cy"
					from={startY}
					to={endY}
					dur={`${ballDur}s`}
					fill="freeze"
				/>
			</circle>

			{phases.map((phase, phaseIndex) => {
				const cy = startY + phaseIndex * gapY
				const phaseTime = phaseIndex * phaseInterval
				const lineStartX = spineX + 6
				const lineEndX = routeStartX + (phase.routes - 1) * routeSpacing
				const lineLength = lineEndX - lineStartX

				return (
					<g key={phase.label}>
						<circle
							cx={spineX}
							cy={cy}
							r="7"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.6"
							strokeOpacity="0.55"
						/>
						<circle
							cx={spineX}
							cy={cy}
							r="3"
							fill="currentColor"
							fillOpacity="0.25"
						>
							<animate
								attributeName="fillOpacity"
								from="0.25"
								to="0.95"
								dur="0.3s"
								begin={`${phaseTime}s`}
								fill="freeze"
							/>
						</circle>
						<text
							x={spineX - 18}
							y={cy + 4}
							textAnchor="end"
							fontSize="12"
							fill="rgba(255,255,255,0.62)"
						>
							{phase.label}
						</text>

						<line
							x1={lineStartX}
							y1={cy}
							x2={lineEndX}
							y2={cy}
							stroke="currentColor"
							strokeWidth="0.8"
							strokeOpacity="0.32"
							strokeLinecap="round"
							strokeDasharray={lineLength}
							strokeDashoffset={lineLength}
						>
							<animate
								attributeName="stroke-dashoffset"
								from={lineLength}
								to="0"
								dur={`${lineDrawDur}s`}
								begin={`${phaseTime}s`}
								fill="freeze"
								calcMode="spline"
								keySplines="0.4 0 0.2 1"
							/>
						</line>

						{Array.from({ length: phase.routes }).map((_, routeIndex) => {
							const rx = routeStartX + routeIndex * routeSpacing
							const fillPeak = dotIntensity(phaseIndex, routeIndex)
							const strokePeak = Math.min(fillPeak * 0.78, 0.82)
							const dotBegin =
								phaseTime +
								lineDrawDur * 0.35 +
								dotOffset(phaseIndex, routeIndex)

							return (
								<circle
									key={`${phase.label}-${routeIndex}`}
									cx={rx}
									cy={cy}
									r="4"
									fill="currentColor"
									fillOpacity="0.1"
									stroke="currentColor"
									strokeWidth="0.6"
									strokeOpacity="0.18"
								>
									<animate
										attributeName="fillOpacity"
										from="0.1"
										to={fillPeak}
										dur={`${subdotDur}s`}
										begin={`${dotBegin}s`}
										fill="freeze"
									/>
									<animate
										attributeName="stroke-opacity"
										from="0.18"
										to={strokePeak}
										dur={`${subdotDur}s`}
										begin={`${dotBegin}s`}
										fill="freeze"
									/>
								</circle>
							)
						})}
					</g>
				)
			})}

			<text
				x="280"
				y="398"
				textAnchor="middle"
				fontSize="12"
				fill="rgba(255,255,255,0.38)"
			>
				5 fases · un programa completo con hitos
			</text>
		</svg>
	)
}
